/* eslint-disable no-console */
import { ApolloClient, InMemoryCache, ApolloLink } from '@apollo/client';
import { getOperationDefinition } from '@apollo/client/utilities';
import { RetryLink } from '@apollo/client/link/retry';
import { onError } from '@apollo/client/link/error';
import createUploadLink from 'apollo-upload-client/public/createUploadLink.js';
import possibleTypes from './possibleTypes.json';
import * as Sentry from '@sentry/browser';

// Denials/validation failures the backend raises as ordinary, expected outcomes of user
// action -- not application bugs. Downgraded to a breadcrumb (kept for debugging context)
// instead of a full Sentry event: app/front/initializers/raven.js sets no sampleRate, and its
// beforeSend (dropNetworkErrors) only drops connectivity failures, so nothing there would stop
// one event per occurrence -- every mistyped form field, every Pundit denial -- at 100%.
// BUSINESS_RULE_VIOLATION is included on the same reasoning (a rejected domain rule is an
// anticipated outcome, not a bug); revisit if that turns out to hide something worth alerting on.
const EXPECTED_ERROR_CODES = [
  'VALIDATION_ERROR',
  'FORBIDDEN',
  'NOT_FOUND',
  'BUSINESS_RULE_VIOLATION',
];

const authLink = new ApolloLink((operation, forward) => {
  const TOKEN = document.querySelector('meta[name="jwt"]')?.getAttribute('content');
  const CSRF = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      Authorization: `Bearer ${TOKEN}`,
      'X-CSRF-TOKEN': CSRF,
    },
  }));

  return forward(operation);
});

// One anonymous document exists (delete_membership.graphql), and Apollo leaves operationName
// `undefined` for it. Sentry drops an `undefined` tag value silently and stringifies it to
// "undefined" inside a fingerprint, so name it here rather than lose the event in a bucket.
// Tolerates a missing operation too: the unit tests call handleApolloError without one.
const operationNameOf = (operation) => operation?.operationName || 'anonymous';

// Extracted from the errorLink below purely so it's callable directly from a unit test --
// building a real Apollo error requires the full link chain, which this doesn't.
export const handleApolloError = ({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.warn(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

      // A schema rejection (max_depth, max_complexity, max_query_string_tokens, or a document
      // the schema no longer validates) comes back as HTTP 200 with `data: null` — it is not a
      // networkError, so without this it reached no one but the console, and the screen just
      // rendered empty (#8248). These are raised before execution and carry no `path`, which is
      // what discriminates them from a resolver's own GraphQL::ExecutionError below.
      if (path === undefined) {
        Sentry.captureException(new Error(`[GraphQL] ${message}`), {
          tags: {
            graphql_operation: operationNameOf(operation),
            graphql_code: extensions?.code,
          },
          extra: { locations, variables: operation?.variables },
        });
        return;
      }

      if (EXPECTED_ERROR_CODES.includes(extensions?.code)) {
        Sentry.addBreadcrumb({
          category: 'graphql',
          message,
          level: 'warning',
          data: { path, graphqlErrorCode: extensions?.code },
        });
        return;
      }

      Sentry.captureException(new Error(message), {
        tags: { graphqlErrorCode: extensions?.code },
        extra: { path },
      });
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);

    // Without an explicit fingerprint Sentry groups these on the Apollo stack trace alone, which
    // is identical for every operation and every status — DOKEOS-FRONTEND-3NZM held 401s, 403s
    // and 500s from 156 tenants in one 22k-event bucket, and its title named only the status of
    // whichever event happened to arrive last. Operation and status are the two axes that make
    // the group mean something.
    //
    // Apollo types networkError as `Error | ServerParseError | ServerError` and only the latter
    // two carry a statusCode, so a genuine transport failure (offline, DNS, CORS) has none.
    const statusCode = networkError.statusCode ?? 'none';
    const operationName = operationNameOf(operation);

    Sentry.captureException(networkError, {
      tags: {
        graphql_operation: operationName,
        graphql_status: statusCode,
      },
      extra: { variables: operation?.variables },
      fingerprint: ['graphql-network', operationName, String(statusCode)],
    });

    // An expired or invalid session: there is no refresh endpoint, so send the user back
    // to sign in rather than leaving the request to fail silently (closes #7822).
    if (networkError.statusCode === 401) {
      window.location.href = `/${I18n.locale}/users/sign_in`;
    }
  }
};

export const errorLink = onError(handleApolloError);

const uploadLink = createUploadLink({
  uri: `/${I18n.locale}/api/graphql`,
  credentials: 'include',
});

// Only a dead or unreachable upstream can be fixed by asking again. Everything else — a resolver
// that raised, a document the schema rejects, an expired session — returns the same answer on
// attempt five as on attempt one.
const RETRYABLE_STATUS_CODES = [502, 503, 504];

// Exported for api_client.test.js: a pure predicate is worth testing directly rather than
// through a live RetryLink, and both rules below are easy to regress silently.
export const shouldRetry = (error, operation) => {
  if (!error) return false;

  // Only queries are safe to replay. Two thirds of this app's GraphQL documents are mutations,
  // and replaying one re-runs its side effects — resetProgression creates a new Attempt row and
  // re-archives the SCORM context on each run, which is how a single click fired it more than
  // once. This replaces a NON_RETRYABLE_OPERATIONS denylist that named that one mutation out of
  // 340, and which — being keyed on operationName — could never have named an anonymous one.
  if (getOperationDefinition(operation.query)?.operation !== 'query') return false;

  // A 5xx from a resolver is deterministic (see DOKEOS-EJZ, where Mailjet rejects the same
  // malformed filter every time). Replaying it multiplies the Sentry events, adds ~4.5s of
  // backoff before the user learns anything, and loads a backend that is already failing.
  return error.statusCode === undefined || RETRYABLE_STATUS_CODES.includes(error.statusCode);
};

export const retryLink = new RetryLink({
  // A 504 is often a slow resolver rather than a dead upstream, and five attempts multiply the
  // load exactly when the server is least able to take it.
  attempts: { max: 3, retryIf: shouldRetry },
});

// CACHE
function getUserIdFromJwt() {
  try {
    const token = document.querySelector('meta[name="jwt"]')?.getAttribute('content');
    if (!token) return 'anonymous';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.user_id ?? 'anonymous';
  } catch {
    return 'anonymous';
  }
}

export const SESSION_KEY = `dokeos_apollo_cache_${getUserIdFromJwt()}`;

// possibleTypes.json is generated from the schema by `yarn graphql:codegen` (see codegen.yml)
// -- regenerate it after adding or changing a GraphQL interface or union.
const cache = new InMemoryCache({
  possibleTypes: possibleTypes.possibleTypes,
  typePolicies: {
    SurveyChoiceStat: { keyFields: false },
    SurveyTableLabel: { keyFields: false },
    SurveyTableCellStat: { keyFields: false },
    SurveyChoiceRespondent: { keyFields: false },
    QuestionChoiceStat: { keyFields: false },
    SurveyQuestionChoiceStat: { keyFields: false },
    SurveyTableRowStat: { keyFields: false },
    SurveyTableColumnStat: { keyFields: false },
    // portalName is view-scoped (see NamesCustomFilters#ambiguous_filter_names): the export
    // modal asks for it with { view } alone while the filter bar asks with the full
    // statisticsFilter, so normalizing by id lets whichever query answers last overwrite the
    // other's portalName on the same StatisticsCustomFilter:<id> entity.
    StatisticsCustomFilter: { keyFields: false },
    // Every reporting query hangs off the one statisticsQuery object, which has no id. Without a
    // merge policy each response replaces it wholesale, wiping the fields the other queries cached
    // under it, so their hooks refetch: 41 requests instead of 21 on a tab with 20 open questions.
    StatisticsQuery: { merge: true },
  },
});

try {
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (stored) {
    const parsed = JSON.parse(stored);
    // Always fetch fresh pending surveys — they change when participations are deleted server-side
    if (parsed.ROOT_QUERY) {
      delete parsed.ROOT_QUERY['pending_satisfaction_surveys'];
    }
    cache.restore(parsed);
  }
} catch {
  /* ignore parse/access errors */
}

const link = ApolloLink.from([retryLink, authLink, errorLink, uploadLink]);

// CLIENT
const apiClient = new ApolloClient({
  link,
  cache,
});

// for debugging
if (process.env.NODE_ENV === 'development') {
  window.__APOLLO_CLIENT__ = apiClient;
}

export default function getApiClient() {
  return apiClient;
}
